import { useState } from 'react';
import { Collapse, Flex, Text } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';

import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteIndicator } from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { accent, neutral, radius, text as textToken } from 'styles/revampTokens';

import FigmaDeleteTrashRedIcon from '../icons/FigmaDeleteTrashRedIcon';

interface Properties {
  /** Row ids currently checked, from the table's own checkbox column — not
   * Dimensions' `selectedTableCheckBox` (a different, unrelated selection
   * concept this bar previously read by mistake and so never actually
   * appeared). */
  selectedIds: Array<string | number>;
  onClearSelection: () => void;
}

/**
 * Data table equivalent of DimensionsRevamp's `SelectionActionBar` — same
 * "N Selected" + pill actions layout, collapsing in/out with the selection
 * state. Builder Mode has no bulk-delete endpoint like Dimensions'
 * `DeleteBulkDimensionItems`, so Delete here dispatches `DeleteIndicator`
 * once per selected row instead of a single bulk request.
 */
const SelectionActionBar = ({ selectedIds, onClearSelection }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const [deleteModal, setDeleteModal] = useState(false);

  const selectedCount = selectedIds.length;
  const isOpen = selectedCount > 0;

  // Keeps showing the last real count while the bar is collapsing out, so
  // the label doesn't flash to "0 Selected" mid-animation.
  const [displayCount, setDisplayCount] = useState(selectedCount);
  if (selectedCount > 0 && selectedCount !== displayCount) setDisplayCount(selectedCount);

  const handleDelete = async () => {
    setDeleteModal(false);
    await Promise.all(
      selectedIds.map((indicatorId: any) => dispatch(DeleteIndicator({ indicatorId }))),
    );
    onClearSelection();
  };

  return (
    <>
      <Collapse in={isOpen} animateOpacity>
        {/* Renders as the first child inside the table's bordered card, so it
            reads as a full-width toolbar strip (tinted, hairline underneath)
            rather than two controls floating in the card's rounded corner. */}
        <Flex
          alignItems='center'
          justifyContent='space-between'
          gap='16px'
          px='16px'
          py='10px'
          bg={accent.subtle}
          borderBottom='1px solid'
          borderColor={neutral.border}
        >
          <Flex alignItems='center' gap='8px' minW={0}>
            <Text color={accent.solid} fontSize='13px' fontWeight={600} whiteSpace='nowrap'>
              {displayCount} Selected
            </Text>
            <Text
              as='button'
              type='button'
              color={textToken.secondary}
              fontSize='12px'
              fontWeight={500}
              whiteSpace='nowrap'
              textDecoration='underline'
              cursor='pointer'
              _hover={{ color: textToken.primary }}
              onClick={onClearSelection}
            >
              Clear
            </Text>
          </Flex>

          <Flex
            as='button'
            type='button'
            alignItems='center'
            gap='7px'
            h='30px'
            bg='white'
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={radius.control}
            px='12px'
            flexShrink={0}
            cursor='pointer'
            transition='background 0.12s ease, border-color 0.12s ease, transform 0.1s ease'
            _hover={{ bg: '#FFF5F3', borderColor: '#FE552F' }}
            _active={{ transform: 'scale(0.96)' }}
            onClick={() => setDeleteModal(true)}
          >
            <FigmaDeleteTrashRedIcon width={11} height={13} />
            <Text color='#FE552F' fontSize='13px' fontWeight={500} lineHeight='1'>
              Delete
            </Text>
          </Flex>
        </Flex>
      </Collapse>

      <DeleteGroupModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        itemName={selectedCount > 1 ? `${selectedCount} row(s)` : `${selectedCount} row`}
        onDelete={handleDelete}
        orgChart={true}
        isBuilderMode={true}
      />
    </>
  );
};

export default SelectionActionBar;
